package raw.models

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

typealias CategoryId = String

// =================================================================
// 1. CANONICAL TAXONOMY (The "Rosetta Stone")
// =================================================================

/**
 * The single source of truth for all spending categories.
 * All logic in cards.json and merchants.json MUST map to these keys.
 * This ensures "DINING" always means the same thing across all banks.
 */
@Serializable
enum class SpendCategory {
    // TRAVEL
    TRAVEL_GENERAL,
    TRAVEL_FLIGHT,
    TRAVEL_HOTEL,
    TRAVEL_RAIL,
    TRAVEL_BUS,
    TRAVEL_CAB,

    // DINING
    DINING, // Restaurants, Cafes, Fast Food

    // SHOPPING
    SHOPPING_GENERAL,
    SHOPPING_GROCERY,
    SHOPPING_ELECTRONICS,
    SHOPPING_FASHION,
    SHOPPING_PHARMA,

    // BILLS & UTILITIES
    UTILITY_GENERAL,
    UTILITY_ELECTRICITY,
    UTILITY_TELECOM, // Mobile/Broadband
    UTILITY_BROADBAND,
    UTILITY_GAS,

    // FINANCIAL & OTHERS
    FUEL,
    ENTERTAINMENT,
    WALLET_LOAD,
    RENT,
    INSURANCE,
    GOVERNMENT,
    EDUCATION,
    EMI,
    UNCATEGORIZED
}

/* -- this is not in use, Transaction is used now
@Serializable
data class TransactionRequest(
    val amount: Double,
    val merchantId: String? = null,
    val categoryId: String,
    val channel: String // Expects "ONLINE" or "OFFLINE"
)*/

@Serializable
data class Category(
    val id: String,

    @SerialName("display_name") // Maps JSON "display_name" -> Kotlin "displayName"
    val displayName: String,

    @SerialName("parent_id")
    val parentId: String? = null,

    @SerialName("ui_config")
    val uiConfig: CategoryUiConfig,

    // Optional field present in some JSON entries (like 'cat_online_shopping')
    val description: String? = null
)

@Serializable
data class CategoryUiConfig(
    val icon: String,
    val color: String,

    @SerialName("sort_order")
    val sortOrder: Int
)

// 3. MERCHANT MAPPING (For Logic)
@Serializable
data class MerchantMapping(
    val id: String,
    val names: List<String>,
    val mcc: List<String> = emptyList(),
    @SerialName("category_id") val categoryId: CategoryId
)
/**
 * Represents a resolved category profile for a merchant.
 * Supports ambiguity (e.g., 90% Shopping, 10% Utility).
 */
@Serializable
data class CategoryProfile(
    val primary: CategoryId,
    val merchantId: String? = null,
    val weightedMatches: List<WeightedCategory> = emptyList()
)

@Serializable
data class WeightedCategory(
    val category: CategoryId,
    val confidence: Double // 0.0 to 1.0 (Trust Score)
)

// =================================================================
// 2. CORE ENTITIES (Card Structure)
// =================================================================

@Serializable
data class CreditCard(
    val id: String,
    val name: String,
    @SerialName("reward_currency") val rewardCurrency: RewardCurrency,
    @SerialName("valuation_spectrum") val valuationSpectrum: List<ValuationPoint>,
    @SerialName("product_info") val productInfo: ProductInfo,

    val rules: List<CardRule>,
    val caps: Map<String, CapDefinition>? = null,
    val exclusions: List<Exclusion> = emptyList()

    // Optional but needed if you are processing caps

)

@Serializable
data class CapDefinition(
    val limit: Double,
    val period: String,
    val unit: String
)


@Serializable
data class ProductInfo(
    val issuer: String,
    val networks: List<String>, // 🟢 Expects the array ["Visa", "RuPay"]
    val tier: String,
    @SerialName("image_url") val imageUrl: String? = null,
    val fees: CardFees? = null,
    @SerialName("transfer_partners") val transferPartners: List<TransferPartner>? = null
)

@Serializable
data class TransferPartner(
    val name: String,
    val ratio: String, // e.g., "1:1", "2:1"

    @SerialName("estimated_value")
    val estimatedValue: Double? = null // Optional: e.g., 1.0 (The resulting value in INR per point)
)

@Serializable
data class CardFees(
    val annual: Double,
    @SerialName("forex_markup") val forexMarkup: Double
)

@Serializable
data class Merchant(
    val id: String,

    @SerialName("display_name")
    val displayName: String,

    // Hidden keywords for search (e.g., "Bonvoy" for "Marriott")
    @SerialName("search_keywords")
    val searchKeywords: List<String> = emptyList(),

    @SerialName("default_category_id") val defaultCategoryId: String? = null,

    @SerialName("supported_categories") val supportedCategories: List<String>? = null,
    val presence: MerchantPresence = MerchantPresence.HYBRID_PREFER_ONLINE,

    // List of MCC codes this merchant is known to use (for backend engine)
    val mcc: List<String> = emptyList(),

    @SerialName("logo_url")
    val logoUrl: String? = null
) {
    // Helper to check if a user's search query matches this merchant
    fun matchesQuery(query: String): Boolean {
        if (query.isBlank()) return false
        val cleanQuery = query.trim().lowercase()
        val cleanName = displayName.lowercase()

        // 1. Direct Name Match (Bidirectional)
        // matches if input is "Indigo" (substring) OR "Indigo Airlines" (superstring)
        if (cleanName.contains(cleanQuery) || cleanQuery.contains(cleanName)) {
            return true
        }

        // 2. Keyword Match (Bidirectional)
        return searchKeywords.any { keyword ->
            val k = keyword.lowercase()
            // Check both: Input-inside-Keyword OR Keyword-inside-Input
            k.contains(cleanQuery) || cleanQuery.contains(k)
        }
    }
}

@Serializable
enum class MerchantPresence {
    @SerialName("ONLINE_ONLY")
    ONLINE_ONLY,

    @SerialName("OFFLINE_ONLY")
    OFFLINE_ONLY,

    @SerialName("HYBRID_PREFER_ONLINE")
    HYBRID_PREFER_ONLINE,

    @SerialName("HYBRID_PREFER_OFFLINE")
    HYBRID_PREFER_OFFLINE;
}
// =================================================================
// 3. PROGRAM RULES (The Logic Configuration)
// =================================================================

@Serializable
data class ProgramRules(
    @SerialName("base_rate") val baseRate: Double, // e.g., 0.033 (3.3%)
    val multipliers: List<MultiplierRule> = emptyList(),
    val caps: Map<String, CapRule> = emptyMap(), // Key: "smartbuy_cap" -> Rule
    val exclusions: List<ExclusionRule> = emptyList()
)

@Serializable
data class MultiplierRule(
    val id: String,
    val matcher: RuleMatcher,
    val rate: Double, // e.g., 0.165 (16.5%)
    @SerialName("cap_ref") val capRef: String? = null // References a key in the 'caps' map
)

@Serializable
data class CapRule(
    val limit: Double,
    val period: String, // "MONTHLY", "QUARTERLY", "YEARLY"
    val unit: String // "POINTS", "CURRENCY"
)

@Serializable
data class ExclusionRule(
    val type: String, // "CATEGORY", "MCC"
    val value: String? = null,
    val values: List<String>? = null
)

// =================================================================
// 4. RULE MATCHING (The "If-This-Then-That")
// =================================================================

@Serializable
data class RuleMatcher(
    val type: MatcherType,
    val value: String? = null, // Single value match
    val values: List<String>? = null, // List match (OR logic)
    @SerialName("sub_categories") val subCategories: List<String>? = null // For ROUTE_ID matchers that act as categories
)

@Serializable
enum class MatcherType {
    ROUTE_ID,       // Matches a specific RewardRoute (e.g., "smartbuy")
    CATEGORY,       // Matches a SpendCategory (e.g., "DINING")
    MERCHANT_NAME,  // Matches raw merchant name (e.g., "Uber")
    MERCHANT_ID,
    CHANNEL,        // Matches "ONLINE" or "OFFLINE"
    MCC             // Matches specific MCC codes
}

// =================================================================
// 5. REWARD PATH (The Runtime Decision Object)
// =================================================================
@Serializable
data class RouteDefinition(
    val id: String,
    val name: String,
    val type: String, // "PORTAL", "VOUCHER_AGGREGATOR"
//    val constraints: RouteConstraints = RouteConstraints(),
//    @SerialName("supported_merchants") val supportedMerchants: List<String> = emptyList(),
    @SerialName("merchant_constraints")
    val merchantConstraints: MerchantConstraints = MerchantConstraints(),
    val description: String? = null, // 🟢 NEW
    val link: String? = null
)

@Serializable
data class MerchantConstraints(
    @SerialName("allowed_merchants")
    val allowedMerchants: List<String> = emptyList(),
    @SerialName("allowed_categories")
    val allowedCategories: List<String> = emptyList()
)

@Serializable
data class RouteConstraints(
    // Define all possible constraint fields here with defaults
    @SerialName("mcc_passthrough") val mccPassthrough: Boolean = false,
    @SerialName("requires_login") val requiresLogin: Boolean = false,
    @SerialName("default_fee_percent") val defaultFeePercent: Double = 0.0,
    @SerialName("surcharge_merchants") val surchargeMerchants: List<String> = emptyList()
)


/**
 * The atomic unit of the Strategy Engine.
 * A Card expands into multiple paths at runtime (Direct, Portal, Voucher).
 * This object is generated by the engine, not stored in JSON.
 */
//@Serializable
//data class RewardPath(
//    val id: String,          // Generated ID: "HDFC_INFINIA_SMARTBUY_VOUCHER"
//    val cardId: String,
//    val route: RewardRoute,  // The method used (Direct, Portal, Voucher)
//    val multiplier: Double,  // The rate applied (e.g., 0.165)
//    val cap: CapRule? = null // The specific cap rule that applies to this path
//)

/**
 * Describes the "Method" of spending.
 */
@Serializable
sealed class RewardRoute {
    // 1. Standard Swipe/Tap
    abstract val name: String
    abstract val description: String? // 🟢 NEW: Abstract property
    abstract val link: String?        // 🟢 NEW: Abstract property

    object Direct : RewardRoute() {
        override fun toString() = "Direct Transaction"
        override val name = "Direct Swipe"
        override val description: String? = null // 🟢 Must be overridden
        override val link: String? = null        // 🟢 Must be overridden
    }

    object Upi : RewardRoute() // 🟢 NEW: UPI is now an official route
    {
        override val name="UPI"
        override val description: String? = null
        override val link: String? = null
    }

    // 2. Shopping via Bank Portal (SmartBuy, Travel Edge)
    data class Portal(
        val id: String,
        override val name: String,
        override val description: String? = null,
       override val link: String? = null        // 🟢 Must be overridden
    ) : RewardRoute()

    // 3. Buying a Gift Card/Voucher first
    data class Voucher(
        val id: String,
        override val name: String,
        val aggregator: String, // "Gyftr", "Amazon"
        val brand: String,       // "Amazon Pay", "Myntra"
        override val description: String? = null,
        override val link: String? = null        // 🟢 Must be overridden
    ) : RewardRoute()

    data class AppPayment(
        val id: String,
        override val name: String,
        override val description: String? = null,
        override val link: String? = null        // 🟢 Must be overridden
    ) : RewardRoute()

    // 2. For wallets like "Amazon Pay Balance" or "Paytm Wallet"
    // Usage: "Load Money INTO Amazon Pay Wallet"
    data class Wallet(
        val id: String,
        override val name: String,
        override val description: String? = null,
        override val link: String? = null        // 🟢 Must be overridden
    ) : RewardRoute()

    val displayName: String
        get() = when (this) {
            is Direct -> "Direct Swipe"
            is Upi -> "UPI Scan & Pay"
            is Portal -> name
            is Voucher -> "Voucher ($brand)"
            is AppPayment -> name
            is Wallet -> name
        }


}

// =================================================================
// 6. SIMULATION RESULTS (The Final Output)
// =================================================================

/**
 * The final calculated result for a specific path.
 * This is what the UI consumes to display the "Winner".
 */
@Serializable
data class SimulationResult(
    val path: RewardPath,      // Contains the Card + Route info
    val netPoints: Double,     // Points earned AFTER caps are applied
    val valueInr: Double,      // Monetary value of the points
    val isCapped: Boolean,     // Did this transaction hit a cap?
    val appliedRuleName: String, // e.g., "SmartBuy 5X Accelerator"
    val confidence: Double     // Trust Score (0.0 - 1.0) based on category ambiguity
) {
    // Helpers for UI display
    val cardName: String get() = path.cardId // Note: UI should map this ID to a pretty name from a separate store/cache

    val routeDescription: String get() = when(path.route) {
        is RewardRoute.Direct -> "Base Reward Rate"
        is RewardRoute.Portal -> "via ${(path.route as RewardRoute.Portal).name}"
        is RewardRoute.Voucher -> "Buy ${(path.route as RewardRoute.Voucher).brand} Voucher"
        // ✅ NEW BRANCHES ADDED
        is RewardRoute.AppPayment -> "Pay via ${(path.route as RewardRoute.AppPayment).name}"
        is RewardRoute.Wallet -> "Load ${(path.route as RewardRoute.Wallet).name} Wallet"
        is RewardRoute.Upi -> "Scan and Pay via UPI"
    }
}

@Serializable
data class RewardPath(
    val id: String,
    val cardId: String,
    val route: RewardRoute,

    // 🟢 1. Made nullable. If a category is excluded, there is no rule to apply.
    val rule: CardRule?,

    // 🟢 2. Added to pass the "Rule: Base Earn Rate" text down to the math breakdown
    val receiptPrefix: String = "",

    // 🟢 3. The Poison Pill flag. If true, simulatePath instantly returns 0 points.
    val isPoisoned: Boolean = false
)

@Serializable
data class RedemptionRatios(
    @SerialName("cash_value") val cashValue: Double,
    @SerialName("travel_value") val travelValue: Double
)

@Serializable
data class RewardBlock(
    @SerialName("spend_required") val spendRequired: Int,
    @SerialName("points_awarded") val pointsAwarded: Int
)

@Serializable
data class Exclusion(
    val type: String,
    val values: List<String>,
    val message: String=""
)

@Serializable
data class CardRule(
    val id: String,
    val name: String? = null, // 🟢 UI-friendly rule name
    val description: String? = null, // 🟢 UI-friendly description
    val matcher: Matcher,
    @SerialName("channel_requirement") val channelRequirement: String? = null,
    val rate: Double? = null,
    @SerialName("reward_block") val rewardBlock: RewardBlock? = null,
    @SerialName("accelerated_multiplier") val acceleratedMultiplier: Double? = null, // 🟢 For Base/Bonus Split
    @SerialName("allowed_networks") val allowedNetworks: List<String>? = null, // 🟢 The UPI/Network Bouncer
    @SerialName("cap_ref") val capRef: String? = null
)

@Serializable
data class RewardCurrency(
    val name: String,
    val type: String // e.g., "CASH", "POINTS", "MILES", "RESTRICTED_ECOSYSTEM"
)

@Serializable
data class ValuationPoint(
    val label: String,
    @SerialName("value_inr") val valueInr: Double,
    val liquidity: String // e.g., "CASH", "TRAVEL", "LOCKED", "LIFESTYLE"
)

@Serializable
data class Matcher(
    /**
     * The type of matching logic to apply.
     * Expected values: "CATEGORY", "ROUTE_ID", "MERCHANT_ID", "MCC", "MERCHANT_NAME", "CHANNEL"
     */
    val type: String,

    /**
     * Used for exact, single-string matches.
     * e.g., "value": "route_hdfc_smartbuy_gyftr"
     */
    val value: String? = null,

    /**
     * Used when a rule applies to multiple targets.
     * e.g., "values": ["UTILITY", "UTILITY_ELECTRICITY"]
     */
    val values: List<String>? = null,

    /**
     * Used specifically with ROUTE_ID to restrict a portal multiplier
     * to certain spend categories.
     * e.g., "sub_categories": ["cat_flights", "cat_hotels"]
     */
    @SerialName("sub_categories")
    val subCategories: List<String>? = null
)

@Serializable
data class CalculationResult(
    val cardId: String,
    val cardName: String,
    val routeName: String,
    val routeType: String,
    val rawPointsEarned: Int,
    val basePoints: Double,       // 🟢 Added
    val bonusPoints: Double,      // 🟢 Added
    val maxYieldInr: Double,      // 🟢 Replaces cashValue/travelValue
    val mathBreakdown: String,
    val isCapped: Boolean,
    val spectrumOptions: List<SpectrumResult>, // 🟢 Passed to UI for dropdowns
    val currencyName: String,
    val routeDescription: String? = "",
    val routeLink: String? = ""
)

@Serializable
data class SpectrumResult(
    val label: String,
    val liquidity: String,
    val inrValue: Double,
    val redemptionLogic: String
)



data class DirectResolution(
    val rule: CardRule?,
    val receiptPrefix: String,
    val isExcluded: Boolean
)